/* eslint-disable no-param-reassign */
import React from 'react';
import i18next from 'i18next';

import { PrinterScript, formatZReport } from 'Systems/PrinterScript/exports';
import { addWarning, dismissType, addError } from 'actions/Error';
import {
  getCurrentPrinterIntegration,
  getSetting,
  getUseReceiptTemplates,
} from 'reducers/configs/settings';
import {
  prepareZReportData,
  buildZReportPatchscript,
} from 'actions/integrations/printer/preparePrintoutData';
import { getTemplateForType } from 'reducers/patchScript';
import { INTEGRATIONS, INTEGRATION_TYPES } from 'constants/CAFA';
import {
  getCafaEntry,
  getGeneralPrintingSettings,
  getIsLoadingCafa,
} from 'reducers/cafaConfigs';
import { getPluginLifecycleHook } from 'reducers/Plugins';
import { getConnectionHealth } from 'reducers/connectivity/connection';
import { composeReceiptWithSchema } from 'reducers/patchScript/composeReceiptWithSchema';
import { doClientRequest } from 'services/ErplyAPI/core/ErplyAPI';
import { getSelectedPosID } from 'reducers/PointsOfSale';
import { sleep } from 'utils';
import { printingStatistics } from 'utils/statistics/PrintingStatistics';
import { STATISTIC_ENTRY_TYPES } from 'utils/statistics/Statistics';

import {
  MSActions,
  WrapperActions,
  BrowserActions,
  renderElement,
  renderPatchscript,
  wrapPngData,
} from './actions';
import t from './testPrints.module.scss';
import { offlinePrint, offlineBrowserPrint } from './offlinePrint';
import { generateReceipt } from './generateReceipt';
import { Options, SalesDocument } from './types';

/**
 * @param {SalesDocument & {payments: any}} salesDocument
 * @param {Options} options
 * Given a sales document, gathers up the necessary data for it and prints out
 * a sales receipt.
 * May make new network requests to fetch additional data about the document
 * @returns {Promise<void>}
 */

/** Workaround until PBIB-5483 */
function waitForCafaLoaded() {
  return async function(dispatch, getState) {
    let loading = getIsLoadingCafa(getState());
    while (loading) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(1);
      loading = getIsLoadingCafa(getState());
    }
  };
}

export const printReceipt = (
  { payments, ...salesDocument },
  options = {},
) => async (dispatch, getState) => {
  const { before, on, after } = getPluginLifecycleHook('onPrintReceipt')(
    getState(),
  );
  printingStatistics.init({
    description: 'Waiting for CAFA to load',
    itemType: STATISTIC_ENTRY_TYPES.TypeNameDownload,
  });
  await dispatch(waitForCafaLoaded());
  printingStatistics.finishLog('Waiting for CAFA to load');
  const pointOfSaleID = getSelectedPosID(getState());
  const useReceiptTemplates = getUseReceiptTemplates(getState());
  const isOnline = getConnectionHealth(getState());

  const pluginCancel = new Error('Cancelled by plugin');
  const convertToPluginError = e => {
    console.error(e);
    throw pluginCancel;
  };
  try {
    await dispatch(before({ payments, salesDocument, options })).catch(
      convertToPluginError,
    );

    dispatch(
      addWarning(i18next.t('alerts:printing.loadingDocumentData'), {
        selfDismiss: false,
        dismissible: false,
        errorType: 'receipt_print',
      }),
    );
    const receiptAsLazyPromises = await dispatch(
      generateReceipt({ salesDocument, options }),
    );

    const onlyBrowserPrint = await receiptAsLazyPromises.onlyBrowserPrint;
    if (await onlyBrowserPrint) {
      if (useReceiptTemplates) {
        printingStatistics.init({
          description:
            'Printing only browser print using Actual Reports templates',
          itemType: STATISTIC_ENTRY_TYPES.TypeNameDataProcessing,
        });
        const html = await receiptAsLazyPromises.html;
        return dispatch(BrowserActions.print({ fullHtml: html })).finally(
          () => {
            printingStatistics.finishLog(
              'Printing only browser print using Actual Reports templates',
            );
            return dispatch(dismissType('receipt_print'));
          },
        );
      }
      const url = await receiptAsLazyPromises.url;
      printingStatistics.init({
        description: 'Printing only browser print using receipt link',
        itemType: STATISTIC_ENTRY_TYPES.TypeNameDataProcessing,
      });
      return dispatch(BrowserActions.print({ url })).finally(() => {
        printingStatistics.finishLog(
          'Printing only browser print using receipt link',
        );
        return dispatch(dismissType('receipt_print'));
      });
    }

    const type = INTEGRATION_TYPES.printer;

    const useWrapper =
      getCafaEntry('wrapper', type)(getState())?.value.enabled ?? false;
    const useMS =
      getCafaEntry('goMS', type)(getState())?.value.enabled ?? false;
    const useBrowser =
      getCafaEntry('browser', type)(getState())?.value.enabled ?? false;

    const funcs = [
      useWrapper &&
        (async dispatch => {
          const [patchScript, url] = await Promise.all([
            receiptAsLazyPromises.patchScript,
            receiptAsLazyPromises.url,
          ]);
          return dispatch(
            WrapperActions.printReceipt({ patchScript, link: url }, options),
          );
        }),
      useWrapper &&
        (async dispatch => {
          const patchScript = await receiptAsLazyPromises.patchScript;
          return dispatch(WrapperActions.printPatchscript(patchScript));
        }),
      useMS &&
        (async dispatch => {
          const saleDocumentId = await receiptAsLazyPromises.saleDocumentId;
          if (saleDocumentId) {
            printingStatistics.init({
              description: `Printing MS receipt by saleId (${saleDocumentId})`,
              itemType: STATISTIC_ENTRY_TYPES.TypeNameDataProcessing,
            });
            try {
              // Try to use new commands without checking if installed ms version
              // supports said command. Purpose of the new commands is to speed up
              // printing but checking ms version would require additional
              // network request which would partially negate the performance gained
              // by using new commands.
              await dispatch(MSActions.printReceiptBySaleId(saleDocumentId));
              printingStatistics.finishLog(
                `Printing MS receipt by saleId (${saleDocumentId})`,
              );
              return;
            } catch (error) {
              const msg = (() => {
                if ('message' in error) {
                  return error.message;
                }
                if (typeof error === 'string') {
                  return error;
                }
                return undefined;
              })();
              printingStatistics.finishLog(
                `Printing MS receipt by saleId (${saleDocumentId})`,
                msg,
              );
              // Fallthrough
              // Assume that error occurred because installed MS version
              // only supports printing if patchscript is provided
            }
          }
          printingStatistics.init({
            description: `Printing MS receipt by generating patchscript (${saleDocumentId})`,
            itemType: STATISTIC_ENTRY_TYPES.TypeNameDataProcessing,
          });
          const patchScript = await receiptAsLazyPromises.patchScript;
          const patchScriptAsString = JSON.stringify(patchScript);
          try {
            await dispatch(MSActions.print(patchScriptAsString));
          } finally {
            printingStatistics.finishLog(
              `Printing MS receipt by generating patchscript (${saleDocumentId})`,
            );
          }
        }),
      useBrowser &&
        (async dispatch => {
          const hasActualReportsTemplate = await receiptAsLazyPromises.hasActualReportsTemplate;
          if (hasActualReportsTemplate) {
            printingStatistics.init({
              description: `Printing Browser receipt via Actual Reports Template`,
              itemType: STATISTIC_ENTRY_TYPES.TypeNameDataProcessing,
            });
            const html = await receiptAsLazyPromises.html;
            try {
              return dispatch(BrowserActions.print({ fullHtml: html }));
            } finally {
              printingStatistics.finishLog(
                `Printing Browser receipt via Actual Reports Template`,
              );
            }
          }
          printingStatistics.init({
            description: `Printing Browser receipt via PatchScript`,
            itemType: STATISTIC_ENTRY_TYPES.TypeNameDataProcessing,
          });
          const patchScript = await receiptAsLazyPromises.patchScript;
          try {
            return dispatch(BrowserActions.printPatchScript(patchScript));
          } finally {
            printingStatistics.finishLog(
              `Printing Browser receipt via PatchScript`,
            );
          }
        }),
    ].filter(a => a);

    // If we want to prioritize performance then printing should start
    // before plugin on hook is called because the plugin hook waits for patchscript
    const printPromise = Promise.all(funcs.map(dispatch))
      .catch(e => console.warn('Failed to print with error', e))
      .finally(() => {
        doClientRequest({
          request: 'registerReceiptPrint',
          invoiceID: salesDocument.id ?? salesDocument.invoiceID,
          pointOfSaleID,
        }).catch(err =>
          dispatch(
            addError(
              err instanceof Error
                ? err.message
                : 'Failed to register receipt print',
            ),
          ),
        );
      });

    const { patchScript } = receiptAsLazyPromises;
    await dispatch(
      on(
        { payments, salesDocument, options },
        { data: patchScript, patchScript },
      ),
    ).catch(convertToPluginError);

    dispatch(after({ payments, salesDocument, options })).catch(
      convertToPluginError,
    );
    await printPromise;
    dispatch(dismissType('receipt_print'));
  } catch (e) {
    if (!isOnline) {
      try {
        await dispatch(offlinePrint(salesDocument, options));
      } catch (error) {
        await dispatch(offlineBrowserPrint(salesDocument, options));
      }
    } else if (e !== pluginCancel) {
      console.error('Printing failed with reason', e);
      dispatch(addError('Printing failed, see console for details'));
    }
    dispatch(dismissType('receipt_print'));
  }
};

export const printGiftReceipt = salesDocument =>
  printReceipt(salesDocument, { giftReceipt: true });
export const printKitchenReceipt = salesDocument =>
  printReceipt(salesDocument, { kitchen: true });
export const printBarReceipt = salesDocument =>
  printReceipt(salesDocument, { bar: true });
/**
 * Given some params for getZReport, will print out the respective Z-Report
 */
export const printZReport = (html, onlyBrowserPrint = false) => async (
  dispatch,
  getState,
) => {
  const data = await dispatch(
    prepareZReportData({
      getShortReport: getSetting('touchpos_get_short_report')(getState()),
    }),
  );
  await dispatch(waitForCafaLoaded());
  const {
    zReport: { useBoReceipt },
    forcePatchscriptRender: { enabled, pixelWidth, scaleMultiplier },
  } = getGeneralPrintingSettings(getState());

  const type = INTEGRATION_TYPES.printer;
  const useWrapper =
    getCafaEntry('wrapper', type)(getState())?.value.enabled ?? false;
  const useMS = getCafaEntry('goMS', type)(getState())?.value.enabled ?? false;
  const useBrowser =
    getCafaEntry('browser', type)(getState())?.value.enabled ?? false;

  if (useBoReceipt) {
    if (onlyBrowserPrint) {
      return dispatch(BrowserActions.print({ html }));
    }

    const blob =
      useWrapper || useMS
        ? await renderElement(
            <div
              style={{ color: 'black', background: 'white' }}
              dangerouslySetInnerHTML={{ __html: html }}
            />,
            pixelWidth,
            scaleMultiplier,
          )
        : undefined;
    [
      useWrapper && WrapperActions.printPatchscript([{ type: 'image', blob }]),
      useMS && MSActions.print(JSON.stringify([{ type: 'image', blob }])),
      useBrowser && BrowserActions.print({ html }),
    ]
      .filter(a => a)
      .forEach(dispatch);
    return undefined;
  }

  dispatch(
    addWarning(i18next.t('alerts:printing.printingZReport'), {
      dismissable: false,
      selfDismiss: false,
      errorType: 'zReport',
    }),
  );

  const patchScript = await dispatch(buildZReportPatchscript()).then(
    enabled
      ? data =>
          renderPatchscript(data, pixelWidth, scaleMultiplier).then(wrapPngData)
      : data => Promise.resolve(data),
  );

  const funcs = (onlyBrowserPrint
    ? [BrowserActions.printPatchScript(patchScript)]
    : [
        useWrapper && WrapperActions.printZReport({ html, data }),
        useWrapper && WrapperActions.printPatchscript(patchScript),
        useMS &&
          MSActions.print(
            localStorage.getItem('MS.print.legacy')
              ? await dispatch(formatZReport(data))
              : JSON.stringify(patchScript),
          ),
        useBrowser && BrowserActions.printPatchScript(patchScript),
      ]
  ).filter(a => a);

  if (!funcs.length) {
    dispatch(dismissType('zReport'));
    dispatch(
      addWarning(i18next.t('alerts:printing.noPrintingOption'), {
        selfDismiss: true,
      }),
    );
  }

  Promise.all(funcs.map(dispatch))
    .finally(() => dispatch(dismissType('zReport')))
    .catch(e => dispatch(addError(e.message), { selfDismiss: true }));
};

export const openCashDrawer = () => async (dispatch, getState) => {
  const type = INTEGRATION_TYPES.printer;
  await dispatch(waitForCafaLoaded());
  const useWrapper =
    getCafaEntry('wrapper', type)(getState())?.value.enabled ?? false;
  const useMS = getCafaEntry('goMS', type)(getState())?.value.enabled ?? false;

  const funcs = [
    useWrapper && WrapperActions.openCashDrawer(),
    useMS && MSActions.openCashDrawer(),
  ].filter(a => a);

  try {
    if (funcs.length === 0) {
      throw new Error(i18next.t('alerts:printing.canNotOpenDrawer'));
    }
    await dispatch(
      addWarning(i18next.t('alerts:printing.openingCashDrawer'), {
        dismissable: false,
        selfDismiss: false,
        errorType: 'cashDrawer',
      }),
    );
    await Promise.all(funcs.map(dispatch));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.Error ??
      error.message ??
      'Could not open cash drawer';

    dispatch(
      addError(errorMessage, {
        selfDismiss: 5000,
      }),
    );
  } finally {
    dispatch(dismissType('cashDrawer'));
  }
};

export const printCoupon = data => async (dispatch, getState) => {
  await dispatch(waitForCafaLoaded());
  const patchScriptTemplate = getTemplateForType(
    INTEGRATIONS[INTEGRATION_TYPES.patchScriptTemplate].coupon,
  )(getState());
  const type = INTEGRATION_TYPES.printer;
  const useWrapper =
    getCafaEntry('wrapper', type)(getState())?.value.enabled ?? false;
  const useMS = getCafaEntry('goMS', type)(getState())?.value.enabled ?? false;
  const useBrowser =
    getCafaEntry('browser', type)(getState())?.value.enabled ?? false;

  const {
    forcePatchscriptRender: { enabled, pixelWidth, scaleMultiplier },
  } = getGeneralPrintingSettings(getState());
  const patchScriptData = await (enabled
    ? data =>
        renderPatchscript(data, pixelWidth, scaleMultiplier).then(wrapPngData)
    : data => Promise.resolve(data))(
    composeReceiptWithSchema(patchScriptTemplate, data),
  );
  // prettier-ignore
  const funcs = [
        useWrapper && console.warn('wrapper receiptData/html for coupons not implemented'),
        useWrapper && WrapperActions.printPatchscript(patchScriptData),
        useMS && MSActions.print(
            (localStorage.getItem('MS.print.legacy'))
                ? new PrinterScript(getCurrentPrinterIntegration(getState()))
                    .print('TODO: Implement coupon format for microservice')
                    .ln()
                : JSON.stringify(patchScriptData),
        ),
        useBrowser && BrowserActions.printPatchScript(patchScriptData),
    ].filter(a => a);

  return dispatch(funcs)
    .finally(() => dispatch(dismissType('cashDrawer')))
    .catch(err => dispatch(addError(err.message), { selfDismiss: true }));
};

export const printGiftCard = data => async (dispatch, getState) => {
  await dispatch(waitForCafaLoaded());
  const patchScriptTemplate = getTemplateForType(
    INTEGRATIONS[INTEGRATION_TYPES.patchScriptTemplate].giftCard,
  )(getState());
  const type = INTEGRATION_TYPES.printer;
  const useWrapper =
    getCafaEntry('wrapper', type)(getState())?.value.enabled ?? false;
  const useMS = getCafaEntry('goMS', type)(getState())?.value.enabled ?? false;
  const useBrowser =
    getCafaEntry('browser', type)(getState())?.value.enabled ?? false;

  const {
    forcePatchscriptRender: { enabled, pixelWidth, scaleMultiplier },
  } = getGeneralPrintingSettings(getState());
  const patchScriptData = await (enabled
    ? data =>
        renderPatchscript(data, pixelWidth, scaleMultiplier).then(wrapPngData)
    : data => Promise.resolve(data))(
    composeReceiptWithSchema(patchScriptTemplate, data),
  );
  // prettier-ignore
  const funcs = [
        useWrapper && console.warn('wrapper receiptData/html for gift card not implemented'),
        useWrapper && WrapperActions.printPatchscript(patchScriptData),
        useMS && MSActions.print(
            (localStorage.getItem('MS.print.legacy'))
                ? new PrinterScript(getCurrentPrinterIntegration(getState()))
                    .print('TODO: Implement gift card format for microservice')
                    .ln()
                : JSON.stringify(patchScriptData),
        ),
        useBrowser && BrowserActions.printPatchScript(patchScriptData),
    ].filter(a => a);

  return dispatch(funcs)
    .finally(() => dispatch(dismissType('cashDrawer')))
    .catch(err => dispatch(addError(err.message), { selfDismiss: true }));
};

export const testPrinter = MSActions.testAll;

// DEPRECATED tryPrintRawData
// Internal backwards compatibility
export const tryPrintRawData = MSActions.print;
/**
 * @param {RItem[]} patchScript
 * @param {boolean=} preview
 */
export const printPatchscript = (patchScript, preview = false) => async (
  dispatch,
  getState,
) => {
  await dispatch(waitForCafaLoaded());
  const type = INTEGRATION_TYPES.printer;
  const useWrapper =
    getCafaEntry('wrapper', type)(getState())?.value.enabled ?? false;
  const useMS = getCafaEntry('goMS', type)(getState())?.value.enabled ?? false;
  const useBrowser =
    getCafaEntry('browser', type)(getState())?.value.enabled ?? false;

  const {
    forcePatchscriptRender: { enabled, pixelWidth, scaleMultiplier },
  } = getGeneralPrintingSettings(getState());
  const renderedPatchScript = enabled
    ? await renderPatchscript(patchScript, pixelWidth, scaleMultiplier).then(
        wrapPngData,
      )
    : patchScript;

  if (preview) {
    return renderedPatchScript;
  }

  const funcs = [
    useWrapper && WrapperActions.printPatchscript(renderedPatchScript),
    useMS &&
      !localStorage.getItem('MS.print.legacy') &&
      MSActions.print(JSON.stringify(renderedPatchScript)),
    useBrowser && BrowserActions.printPatchScript(renderedPatchScript),
  ].filter(a => a);
  const results = await Promise.allSettled(funcs.map(dispatch));
  const hasErrors = results.some(result => result.status === 'rejected');
  if (!hasErrors) {
    return renderedPatchScript;
  }
  dispatch(
    addError('Error printing straight patchscript, see console for details'),
    { selfDismiss: true },
  );
  const errors = results
    .filter(result => result.status === 'rejected')
    .map(result => result.reason);
  console.error('Error printing straight patchscript', errors);
};

const Ruler = () => {
  return (
    <div style={{ backgroundColor: 'white', color: 'black' }}>
      Use this ruler to measure the line on the other receipt
      <br />
      <div>Big lines: 100px</div>
      <div>Medium lines: 25px</div>
      <div>Small lines: 5px</div>
      <div className={t.ruler}>
        <div className="line" />
        <div className="line" />
        <div className="line" />
      </div>
    </div>
  );
};
export const printRulerAndStick = () => async (dispatch, getState) => {
  return Promise.all(
    [
      renderElement(<div className={t.top} />, 10000, 1),
      renderElement(<Ruler />, 100, 1),
    ].map(p =>
      p
        .then(blob => [{ type: 'image', blob }])
        .then(ps =>
          Promise.all([
            dispatch(MSActions.print(JSON.stringify(ps))),
            dispatch(WrapperActions.printPatchscript(ps)),
            dispatch(BrowserActions.printPatchScript(ps)),
          ]),
        )
        .catch(e => {
          console.error('Failed to print ruler due to error:', e);
        }),
    ),
  );
};
export const printTextScales = (
  width,
  updateProgress = (done, total) => {},
) => async (dispatch, getState) => {
  const scales = [25, 50, 75, 100, 125, 150, 200, 300, 400];
  const patchScript = [];
  for (let i = 0; i < scales.length; i++) {
    updateProgress(i, scales.length);
    const scale = scales[i];
    // eslint-disable-next-line no-await-in-loop
    const blob = await renderElement(
      <span style={{ background: 'white', color: 'black' }}>
        Text scale {scale}%
      </span>,
      width,
      scale / 100,
    );
    patchScript.push({ type: 'image', blob });
  }

  return Promise.all([
    dispatch(MSActions.print(JSON.stringify(patchScript))),
    dispatch(WrapperActions.printPatchscript(patchScript)),
    dispatch(BrowserActions.printPatchScript(patchScript)),
  ]).catch(e => {
    console.error('Failed to print textscales due to error:', e);
  });
};
